/* eslint-disable @reatom/unit-naming-rule */
import { action, withAssign } from '@reatom/framework'
import Dexie from 'dexie'

import { Array, AsyncStateN, Option, pipe, Result } from '@blade/std-lib'
import { CurrencyModel, CurrencySnapshot } from '@root/entities/currency/model'
import { CurrencyRef } from '@root/entities/currency/model'
import { STORAGE_PREFIX } from '@root/shared'
import { UnknownError } from '@root/shared/core/Error'
import { reatomResource } from '@root/shared/lib'

import type { Network } from '@root/entities/network/model'
import type { Table } from 'dexie'

type StorageKey = [Network.Network, string]
const toStorageKey = (currency: CurrencyRef.Ref): StorageKey => [
  currency.network,
  currency.address,
]

class BlacklistCurrenciesDb extends Dexie {
  hidden!: Table<CurrencySnapshot.Encoded, StorageKey>

  constructor() {
    super(`${STORAGE_PREFIX}/blacklist-currencies`)
    this.version(2).stores({
      hidden: '[network+address]',
    })
  }
}

const db = new BlacklistCurrenciesDb()

const reatomBlacklistCurrenciesLocal = () => {
  const stateAtom = reatomResource(async (ctx) => {
    const stored = await ctx.schedule(() =>
      Result.tryPromise({
        try: () => db.hidden.toArray(),
        catch: (error) => UnknownError.fromUnknown(error),
      }),
    )

    return stored.pipe(
      Result.map((it) =>
        pipe(
          it.map((raw) => {
            const snapshot = pipe(
              CurrencySnapshot.decodeUnknownOption(raw),
              Option.getOrUndefined,
            )
            if (!snapshot) {
              return undefined
            }
            return CurrencyModel.fromSnapshot(snapshot)
          }),
          Array.compact,
        ),
      ),
    )
  })

  const hideAction = action(async (ctx, currency: CurrencyModel) => {
    const snapshot = CurrencyModel.toSnapshot(ctx, currency)
    const result = await ctx.schedule(() =>
      Result.tryPromise({
        try: () =>
          db.hidden.put(CurrencySnapshot.encodeSync(snapshot), [
            currency.network,
            currency.address,
          ]),
        catch: (error) => UnknownError.fromUnknown(error),
      }),
    )
    if (Result.isSuccess(result)) {
      const state = ctx.get(stateAtom)
      AsyncStateN.tap(state, (arr) => {
        if (arr.some((it) => CurrencyModel.eq(it, currency))) {
          return
        }
        stateAtom.setSuccess(ctx, [currency, ...arr])
      })
    }
  }, 'hideCurrencyLocalAction')

  const unhideAction = action(async (ctx, currency: CurrencyRef.Ref) => {
    const result = await ctx.schedule(() =>
      Result.tryPromise({
        try: () => db.hidden.delete(toStorageKey(currency)),
        catch: (error) => UnknownError.fromUnknown(error),
      }),
    )
    if (Result.isSuccess(result)) {
      const state = ctx.get(stateAtom)
      AsyncStateN.tap(state, (arr) => {
        stateAtom.setSuccess(
          ctx,
          arr.filter((it) => !CurrencyRef.eq(it, currency)),
        )
      })
    }
  }, 'unhideCurrencyLocalAction')

  const clearAction = action(async (ctx) => {
    const result = await ctx.schedule(() =>
      Result.tryPromise({
        try: () => db.hidden.clear(),
        catch: (error) => UnknownError.fromUnknown(error),
      }),
    )
    if (Result.isSuccess(result)) {
      const state = ctx.get(stateAtom)
      AsyncStateN.tap(state, () => {
        stateAtom.setSuccess(ctx, [])
      })
    }
  }, 'clearBlacklistCurrenciesLocalAction')

  return stateAtom.pipe(
    withAssign(() => ({
      hide: hideAction,
      unhide: unhideAction,
      clear: clearAction,
    })),
  )
}

export type BlacklistCurrenciesLocalModel = ReturnType<
  typeof reatomBlacklistCurrenciesLocal
>
export const blacklistCurrenciesLocalModel = reatomBlacklistCurrenciesLocal()
